// ui/composables/useFundingSourceVersions.js
import { computed, ref } from 'vue';

import { commitFundingSourceVersion } from '../../application/useCases/commitFundingSourceVersion';
import { retireFundingSource } from '../../application/useCases/retireFundingSource';
import { startFundingSource } from '../../application/useCases/startFundingSource';
import { createVersionTimeline, currentAsOf, scheduledAsOf } from '../../domain/model/VersionTimeline';
import { todayDateStr } from '../../domain/services/effectiveDating';
import {
  appendFundingSourceVersion,
  createFundingSource,
  loadConfiguredFundingSources,
  loadFundingSourceVersions,
  loadFundingSources,
} from '../../infrastructure/api/fundingSourceApi';

/**
 * A clinic's payers, and one payer's version timeline as the editor works with it.
 *
 * THE CONTEXT'S COMPOSITION ROOT for funding sources — the only place that binds the Firestore
 * adapters to the repository port. Everything above it depends on the port; nothing above it imports
 * `infrastructure/`. Same role `useClientCoverages` plays for coverage, and the scope is supplied per
 * operation the same way, rather than captured at construction: a page creating this in `setup()` may
 * not have its clinic yet.
 *
 * IT READS THE CLOCK ONCE, and that is deliberate. `asOf` is captured when the composable is created
 * and never re-read, so a dialog left open across midnight keeps deciding against the date it opened
 * on: the options offered, the floor under the picker, and what counts as "in force" all stay
 * consistent with what the user is looking at. Every rule beneath this point takes `asOf` as an
 * argument, which is what makes them testable without freezing time — see `effectiveDating`, whose
 * `todayDateStr` exists to be called from exactly one layer, this one.
 *
 * `selectedId` is the version being edited, not the version in force. They differ whenever the user
 * switches to a scheduled change, which is the whole point of the version line's menu.
 */

/**
 * @param {{ asOf?: string }} [options] - `asOf` defaults to today and is accepted only so a test or a
 *   caller with its own session date can supply one.
 * @returns {Object}
 */
// eslint-disable-next-line import/prefer-default-export
export function useFundingSourceVersions({ asOf = todayDateStr() } = {}) {
  /**
   * The port, satisfied by the Firestore adapters. Assembled here rather than imported as a ready
   * object so the binding is visible at the one place that owns it.
   *
   * @type {import('../../application/ports/FundingSourceRepository.port').FundingSourceRepository}
   */
  const repository = {
    loadFundingSources,
    loadConfiguredFundingSources,
    loadVersions: loadFundingSourceVersions,
    appendVersion: appendFundingSourceVersion,
    createSeries: createFundingSource,
  };

  /** @type {import('vue').Ref<import('../../domain/model/FundingSource.types').FundingSource[]>} */
  const versions = ref([]);
  const selectedId = ref(null);
  const isBusy = ref(false);

  /** Coordinates only — the record for a version is found in `versions` by id. */
  const timeline = computed(() => createVersionTimeline(versions.value));

  const inForce = computed(() => currentAsOf(timeline.value, asOf));
  const scheduled = computed(() => scheduledAsOf(timeline.value, asOf));

  /**
   * The timeline entry being edited: whatever was selected, else the scheduled change if one exists,
   * else the version in force.
   *
   * Opening on the scheduled change rather than on what applies today is the design's choice, and the
   * reason is that a pending change is the thing a specialist came back to look at — what is in force
   * they can already see in the table.
   */
  const selected = computed(() => {
    const chosen = timeline.value.versions.find(
      (version) => version.versionId === selectedId.value,
    );

    return chosen ?? scheduled.value[0] ?? inForce.value;
  });

  /** The stored record behind the selected entry, or null while nothing is loaded. */
  const selectedRecord = computed(() => {
    return versions.value.find(
      (version) => version.versionId === selected.value?.versionId,
    ) ?? null;
  });

  const readSeries = async (fundingSourceId, clinicId) => {
    versions.value = await repository.loadVersions({ fundingSourceId, clinicId });
    selectedId.value = null;
  };

  return {
    versions,
    timeline,
    inForce,
    scheduled,
    selected,
    selectedRecord,
    isBusy,
    asOf,

    /**
     * The clinic's payers, one row per payer as each stands today.
     *
     * Returned rather than held, because the caller owns the table. Held state here is about ONE
     * payer's timeline; a clinic-wide list is a different question that happens to use the same
     * repository.
     *
     * @param {{ clinicId: string }} scope
     * @returns {Promise<import('../../domain/model/FundingSource.types').FundingSource[]>}
     */
    async loadAll({ clinicId }) {
      isBusy.value = true;
      try {
        return await repository.loadFundingSources(clinicId, asOf);
      } finally {
        isBusy.value = false;
      }
    },

    /**
     * Every payer the clinic has configured, one row each — including one whose first version starts
     * later.
     *
     * The management table's read, kept apart from `loadAll` rather than added to it as a flag,
     * because the two answer different questions and only one of them is safe for a picker: a payer
     * that has not started yet must not be offered as a client's payer today, and must be visible on
     * the page that created it. `loadAll` has three callers and two are pickers.
     *
     * @param {{ clinicId: string }} scope
     * @returns {Promise<import('../../domain/model/FundingSource.types').FundingSource[]>}
     */
    async loadConfigured({ clinicId }) {
      isBusy.value = true;
      try {
        return await repository.loadConfiguredFundingSources(clinicId, asOf);
      } finally {
        isBusy.value = false;
      }
    },

    /**
     * Creates a payer, as its first version on the date the user chose.
     *
     * Kept apart from `commit` because the two are genuinely different writes: one starts a series
     * and has nothing to collide with or supersede, the other places a version on an existing
     * timeline. The user is asked a narrower question here — a new payer starts today or on an
     * earlier date, never a later one — and `startFundingSource` is where that rule lives.
     *
     * `EffectiveDateRejected` reaches the caller, as it does from `commit`: only the dialog can say
     * what happened and keep the user's edits on screen.
     *
     * @param {{ clinicId: string, effectiveFrom: string, record: Object }} request
     * @returns {Promise<string>} the new payer's `fundingSourceId`
     */
    async create({ clinicId, effectiveFrom, record }) {
      isBusy.value = true;
      try {
        return await startFundingSource({
          clinicId, asOf, effectiveFrom, record,
        }, repository);
      } finally {
        isBusy.value = false;
      }
    },

    /**
     * Loads one payer's whole series, tombstones included.
     *
     * @param {{ fundingSourceId: string, clinicId: string }} scope
     */
    async load({ fundingSourceId, clinicId }) {
      isBusy.value = true;
      try {
        await readSeries(fundingSourceId, clinicId);
      } finally {
        isBusy.value = false;
      }
    },

    /** Switches which version the editor is showing. @param {string|null} versionId */
    select(versionId) {
      selectedId.value = versionId;
    },

    /**
     * Saves the record as a version effective on the given date, then re-reads the series so the
     * version line and the options reflect what was just written.
     *
     * Errors are NOT swallowed: `EffectiveDateRejected` and `VersionCollision` both have to reach the
     * caller, which is the only layer that can tell the user what happened and keep the dialog open.
     *
     * @param {{ fundingSourceId: string, clinicId: string, effectiveFrom: string, record: Object }} save
     * @returns {Promise<string>} the new version's id
     */
    async commit({
      fundingSourceId, clinicId, effectiveFrom, record,
    }) {
      isBusy.value = true;
      try {
        const id = await commitFundingSourceVersion({
          fundingSourceId, clinicId, asOf, effectiveFrom, record,
        }, repository);

        await readSeries(fundingSourceId, clinicId);

        return id;
      } finally {
        isBusy.value = false;
      }
    },

    /**
     * Retires the payer as a version effective today.
     *
     * Cancels a scheduled change as a side effect, and the caller has to have said so before calling:
     * a pending version carries `isActive: true` and would otherwise bring the payer back on its own
     * effective date. `scheduled` is exposed above so a confirmation can name it.
     *
     * @param {{ fundingSourceId: string, clinicId: string }} scope
     * @returns {Promise<string>} the retiring version's id
     */
    async retire({ fundingSourceId, clinicId }) {
      isBusy.value = true;
      try {
        const id = await retireFundingSource({ fundingSourceId, clinicId, asOf }, repository);

        // Same re-read as `commit`, and for a sharper reason: retiring also cancels a scheduled
        // version, so without it `scheduled` still names a version that no longer exists and
        // `inForce` still resolves the one just retired.
        await readSeries(fundingSourceId, clinicId);

        return id;
      } finally {
        isBusy.value = false;
      }
    },
  };
} // useFundingSourceVersions()
